import { useState } from 'react';
import './Settings.css';
import { MAP_STYLES, UNIT_SYSTEMS, AIRPORT_DISPLAY_OPTIONS, getMapSettings, saveMapSettings, getColourSettings, saveColourSettings, resetThemeAppearance, getUnitsSettings, saveUnitsSettings, getAirportSettings, saveAirportSettings, DEFAULT_UNITS, DEFAULT_RANGE_RINGS_COUNT, DEFAULT_RANGE_RINGS_INTERVAL, DEFAULT_AIRPORT_DISPLAY, DEFAULT_AIRPORT_COLOUR } from '../utils/mapStyles';
import { resetMaxDistanceOutline } from '../utils/httpApi';
import { getAccessToken } from '../utils/auth';
import { useColumnConfig, useThemeSettings } from '../contexts/SettingsContext';
import { getPresetLabels, getTableConfig } from '../utils/columnConfig';
import { THEMES } from '../utils/themeConfig';
import ColumnConfigurator from './ColumnConfigurator';
import { checkForUpdate, forceReload, getCompiledVersion, getBuildTimestamp } from '../utils/versionCheck';

function Settings({ onSettingsChange, receivers = [], selectedReceiverUuid }) {
  // Map settings (stored in localStorage)
  const [mapSettings, setMapSettings] = useState(() => getMapSettings());
  const [colourSettings, setColourSettings] = useState(() => getColourSettings());
  const [unitsSettings, setUnitsSettings] = useState(() => getUnitsSettings());
  const [airportSettings, setAirportSettings] = useState(() => getAirportSettings());

  // Confirmation modal state - includes receiverUuid for per-receiver resets
  const [confirmModal, setConfirmModal] = useState({ show: false, type: null, receiverUuid: null, receiverName: null, loading: false, error: null, success: null });

  // Column configuration
  const { columnConfig, setPreset, updateTableColumns } = useColumnConfig();
  const [configuratorModal, setConfiguratorModal] = useState({ isOpen: false, tableName: null, tableTitle: null });
  const presetLabels = getPresetLabels();

  // Theme settings
  const { themeSettings, updateTheme } = useThemeSettings();

  // Version / update check state
  const [updateStatus, setUpdateStatus] = useState(null); // null | 'checking' | 'up-to-date' | 'update-available' | 'error' | 'reloading'
  const [remoteVersion, setRemoteVersion] = useState(null);

  const handleCheckForUpdates = async () => {
    setUpdateStatus('checking');
    const result = await checkForUpdate();
    if (!result) {
      setUpdateStatus('error');
    } else if (result.needsUpdate) {
      setRemoteVersion(result.remote.version);
      setUpdateStatus('update-available');
    } else {
      setUpdateStatus('up-to-date');
    }
  };

  const handleForceReload = async () => {
    setUpdateStatus('reloading');
    await forceReload();
  };

  // Check if we have any receivers
  const hasReceivers = receivers.length > 0;

  // Handle map style change
  const handleMapStyleChange = (e) => {
    const newStyle = e.target.value;
    const newSettings = { ...mapSettings, style: newStyle };
    setMapSettings(newSettings);
    saveMapSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange({ mapSettings: newSettings, colourSettings });
    }
  };

  // Handle map brightness change
  const handleMapBrightnessChange = (e) => {
    const newBrightness = parseInt(e.target.value, 10);
    const newSettings = { ...mapSettings, brightness: newBrightness };
    setMapSettings(newSettings);
    saveMapSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange({ mapSettings: newSettings, colourSettings });
    }
  };

  // Handle colour change
  const handleColourChange = (colourKey, newColour) => {
    const newColours = { ...colourSettings, [colourKey]: newColour };
    setColourSettings(newColours);
    saveColourSettings(newColours);
    if (onSettingsChange) {
      onSettingsChange({ mapSettings, colourSettings: newColours });
    }
  };

  // Handle toggle change
  const handleToggleChange = (key, value) => {
    const newColours = { ...colourSettings, [key]: value };
    setColourSettings(newColours);
    saveColourSettings(newColours);
    if (onSettingsChange) {
      onSettingsChange({ mapSettings, colourSettings: newColours });
    }
  };

  // Reset colours to the ACTIVE THEME's defaults, not to DEFAULT_COLOURS.
  // Writing DEFAULT_COLOURS back would store them as explicit per-theme
  // overrides — "reset" would pin the standard palette onto whichever theme is
  // active and the theme's own colours could never return. Dropping the
  // overrides lets the theme reassert itself.
  const handleResetColours = () => {
    resetThemeAppearance();
    const restored = getColourSettings();
    const restoredMap = getMapSettings();
    setColourSettings(restored);
    setMapSettings(restoredMap);
    if (onSettingsChange) {
      onSettingsChange({ mapSettings: restoredMap, colourSettings: restored, unitsSettings });
    }
  };

  // Handle units change
  const handleUnitsChange = (e) => {
    const newUnits = e.target.value;
    const newSettings = { ...unitsSettings, units: newUnits };
    setUnitsSettings(newSettings);
    saveUnitsSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange({ mapSettings, colourSettings, unitsSettings: newSettings });
    }
  };

  // Handle range rings count change
  const handleRangeRingsCountChange = (e) => {
    const newCount = parseInt(e.target.value, 10);
    const newSettings = { ...unitsSettings, rangeRingsCount: newCount };
    setUnitsSettings(newSettings);
    saveUnitsSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange({ mapSettings, colourSettings, unitsSettings: newSettings });
    }
  };

  // Handle range rings interval change
  const handleRangeRingsIntervalChange = (e) => {
    const newInterval = parseInt(e.target.value, 10);
    const newSettings = { ...unitsSettings, rangeRingsInterval: newInterval };
    setUnitsSettings(newSettings);
    saveUnitsSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange({ mapSettings, colourSettings, unitsSettings: newSettings });
    }
  };

  // Reset units to defaults
  const handleResetUnits = () => {
    const defaultSettings = {
      units: DEFAULT_UNITS,
      rangeRingsCount: DEFAULT_RANGE_RINGS_COUNT,
      rangeRingsInterval: DEFAULT_RANGE_RINGS_INTERVAL
    };
    setUnitsSettings(defaultSettings);
    saveUnitsSettings(defaultSettings);
    if (onSettingsChange) {
      onSettingsChange({ mapSettings, colourSettings, unitsSettings: defaultSettings });
    }
  };

  // Handle airport display change
  const handleAirportDisplayChange = (e) => {
    const newDisplay = e.target.value;
    const newSettings = { ...airportSettings, display: newDisplay };
    setAirportSettings(newSettings);
    saveAirportSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange({ mapSettings, colourSettings, unitsSettings, airportSettings: newSettings });
    }
  };

  // Handle airport colour change
  const handleAirportColourChange = (newColour) => {
    const newSettings = { ...airportSettings, colour: newColour };
    setAirportSettings(newSettings);
    saveAirportSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange({ mapSettings, colourSettings, unitsSettings, airportSettings: newSettings });
    }
  };

  // Reset airport settings to defaults
  const handleResetAirportSettings = () => {
    const defaultSettings = {
      display: DEFAULT_AIRPORT_DISPLAY,
      colour: DEFAULT_AIRPORT_COLOUR
    };
    setAirportSettings(defaultSettings);
    saveAirportSettings(defaultSettings);
    if (onSettingsChange) {
      onSettingsChange({ mapSettings, colourSettings, unitsSettings, airportSettings: defaultSettings });
    }
  };

  // Get the interval options based on current unit system
  const currentUnitSystem = UNIT_SYSTEMS[unitsSettings.units];
  const intervalUnit = currentUnitSystem?.distance || 'km';

  // Show confirmation modal for reset
  const showResetConfirmation = (type, receiverUuid, receiverName) => {
    setConfirmModal({ show: true, type, receiverUuid, receiverName, loading: false, error: null, success: null });
  };

  // Close confirmation modal
  const closeConfirmModal = () => {
    setConfirmModal({ show: false, type: null, receiverUuid: null, receiverName: null, loading: false, error: null, success: null });
  };

  // Handle confirmed reset
  const handleConfirmedReset = async () => {
    if (!confirmModal.type || !confirmModal.receiverUuid) return;

    setConfirmModal(prev => ({ ...prev, loading: true, error: null }));

    try {
      const token = getAccessToken();
      const allTime = confirmModal.type === 'allTime';
      const response = await resetMaxDistanceOutline(confirmModal.receiverUuid, allTime, null, token);

      // The service reports the outcome in data.success, and sets it false with an explanatory
      // message when there was nothing to delete. The old condition ORed that together with the
      // transport-level header status, so a perfectly well-formed "no records found" reply still
      // took the success branch and announced "Successfully cleared 0 records." — telling the
      // operator a reset had happened when none had. Trust the service's own verdict.
      if (response.data?.success) {
        const recordsDeleted = response.data?.recordsDeleted || 0;
        setConfirmModal(prev => ({
          ...prev,
          loading: false,
          success: `Successfully cleared ${recordsDeleted} records.`
        }));
        // Auto-close after 2 seconds on success
        setTimeout(() => {
          closeConfirmModal();
        }, 2000);
      } else {
        setConfirmModal(prev => ({
          ...prev,
          loading: false,
          error: response.data?.message || response.header?.statusMessage || 'Failed to reset range data'
        }));
      }
    } catch (err) {
      setConfirmModal(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'An error occurred while resetting'
      }));
    }
  };

  // Handle preset change
  const handlePresetChange = (e) => {
    const presetName = e.target.value;
    if (presetName !== 'custom') {
      setPreset(presetName);
    }
  };

  // Open column configurator
  const openConfigurator = (tableName, tableTitle) => {
    setConfiguratorModal({ isOpen: true, tableName, tableTitle });
  };

  // Close column configurator
  const closeConfigurator = () => {
    setConfiguratorModal({ isOpen: false, tableName: null, tableTitle: null });
  };

  // Apply column configuration changes
  const handleApplyColumns = (visibleColumns, columnOrder) => {
    if (configuratorModal.tableName) {
      updateTableColumns(configuratorModal.tableName, visibleColumns, columnOrder);
    }
  };

  // Handle theme change
  const handleThemeChange = (themeId) => {
    updateTheme(themeId);
    if (onSettingsChange) {
      onSettingsChange({ mapSettings, colourSettings, unitsSettings, airportSettings });
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-container">
        <div className="settings-header">
          <div>
            <h2 className="settings-title">Settings</h2>
            <div className="settings-summary">Configure display preferences for maps and aircraft</div>
          </div>
        </div>

        <div className="settings-card">
          <div className="settings-section">
            <h3 className="section-title">Map Settings</h3>
            <span className="section-note">These settings are saved locally on this device</span>

            <div className="settings-field">
              <label className="field-label">Map Style</label>
              <select
                value={mapSettings.style}
                onChange={handleMapStyleChange}
                className="field-input"
              >
                {Object.entries(MAP_STYLES).map(([key, style]) => (
                  <option key={key} value={key}>{style.name}</option>
                ))}
              </select>
            </div>

            <div className="settings-field">
              <label className="field-label">Map Brightness: {mapSettings.brightness}%</label>
              <div className="brightness-slider-container">
                <span className="brightness-label">Dark</span>
                <input
                  type="range"
                  min="20"
                  max="100"
                  value={mapSettings.brightness}
                  onChange={handleMapBrightnessChange}
                  className="brightness-slider"
                />
                <span className="brightness-label">Light</span>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="section-title">Theme</h3>
            <span className="section-note">Choose a colour theme for the interface</span>

            <div className="theme-cards">
              {Object.entries(THEMES).filter(([key]) => key !== 'custom').map(([key, theme]) => (
                <div
                  key={key}
                  className={`theme-card ${themeSettings.themeId === key ? 'active' : ''}`}
                  onClick={() => handleThemeChange(key)}
                >
                  <div
                    className="theme-card-preview"
                    style={{
                      background: theme.id === 'light'
                        ? 'linear-gradient(90deg, #1a1a2e 30%, #f8f9fa 30%)'
                        : theme.id === 'mono'
                        ? 'linear-gradient(90deg, #1a1a1a 30%, #2a2a2a 30%)'
                        : theme.id === 'dark'
                        ? 'linear-gradient(90deg, #0d1117 30%, #21262d 30%)'
                        : 'linear-gradient(90deg, #3d4751 30%, #d4cfc1 30%)'
                    }}
                  />
                  <div className="theme-card-name">{theme.name}</div>
                  <div className="theme-card-desc">{theme.description}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="settings-section">
            <h3 className="section-title">Units</h3>
            <span className="section-note">Configure display units for distance and altitude</span>

            <div className="settings-row">
              <div className="row-label">Unit System</div>
              <div className="row-controls">
                <select
                  value={unitsSettings.units}
                  onChange={handleUnitsChange}
                  className="field-input compact-select"
                >
                  {Object.entries(UNIT_SYSTEMS).map(([key, system]) => (
                    <option key={key} value={key}>{system.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="unit-info">
              <span>Distance: {currentUnitSystem?.distanceLong}</span>
              <span>Altitude: {currentUnitSystem?.altitudeLong}</span>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="section-title">Airport Display</h3>
            <span className="section-note">Configure airport markers on the map</span>

            <div className="settings-row">
              <div className="row-label">Show Airports</div>
              <div className="row-controls">
                <select
                  value={airportSettings.display}
                  onChange={handleAirportDisplayChange}
                  className="field-input compact-select"
                >
                  {Object.entries(AIRPORT_DISPLAY_OPTIONS).map(([key, option]) => (
                    <option key={key} value={key}>{option.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="settings-row">
              <div className="row-label">Colour</div>
              <div className="row-controls">
                <div className="colour-input-wrapper">
                  <input
                    type="color"
                    value={airportSettings.colour}
                    onChange={(e) => handleAirportColourChange(e.target.value)}
                    className="colour-picker"
                    disabled={airportSettings.display === 'none'}
                  />
                  <input
                    type="text"
                    value={airportSettings.colour}
                    onChange={(e) => handleAirportColourChange(e.target.value)}
                    className="colour-text"
                    maxLength={7}
                    disabled={airportSettings.display === 'none'}
                  />
                </div>
              </div>
            </div>

            <button className="btn-reset" onClick={handleResetAirportSettings}>
              Reset to Defaults
            </button>
          </div>

          <div className="settings-section">
            <h3 className="section-title">Table Columns</h3>
            <span className="section-note">Configure which columns are shown in data tables</span>

            <div className="settings-row">
              <div className="row-label">Preset</div>
              <div className="row-controls">
                <select
                  value={columnConfig?.activePreset || 'standard'}
                  onChange={handlePresetChange}
                  className="field-input compact-select"
                >
                  {Object.entries(presetLabels).map(([key, label]) => (
                    <option key={key} value={key} disabled={key === 'custom'}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="column-config-buttons">
              <button
                className="btn-configure"
                onClick={() => openConfigurator('liveView', 'Live View')}
              >
                Configure Live View
              </button>
              <button
                className="btn-configure"
                onClick={() => openConfigurator('historyAircraft', 'History Aircraft')}
              >
                Configure History Aircraft
              </button>
              <button
                className="btn-configure"
                onClick={() => openConfigurator('historyTracks', 'History Tracks')}
              >
                Configure History Tracks
              </button>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="section-title">Range Rings</h3>
            <span className="section-note">Configure range ring display on the map</span>

            <div className="settings-row">
              <div className="row-label">Number of Rings</div>
              <div className="row-controls">
                <select
                  value={unitsSettings.rangeRingsCount}
                  onChange={handleRangeRingsCountChange}
                  className="field-input compact-select"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="settings-row">
              <div className="row-label">Interval</div>
              <div className="row-controls">
                <select
                  value={unitsSettings.rangeRingsInterval}
                  onChange={handleRangeRingsIntervalChange}
                  className="field-input compact-select"
                >
                  {currentUnitSystem?.intervals.map(interval => (
                    <option key={interval} value={interval}>{interval} {intervalUnit}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Range Rings colour */}
            <div className="settings-row">
              <div className="row-label">Colour</div>
              <div className="row-controls">
                <div className="colour-input-wrapper">
                  <input
                    type="color"
                    value={colourSettings.rangeRings}
                    onChange={(e) => handleColourChange('rangeRings', e.target.value)}
                    className="colour-picker"
                  />
                  <input
                    type="text"
                    value={colourSettings.rangeRings}
                    onChange={(e) => handleColourChange('rangeRings', e.target.value)}
                    className="colour-text"
                    maxLength={7}
                  />
                </div>
              </div>
            </div>

            <button className="btn-reset" onClick={handleResetUnits}>
              Reset to Defaults
            </button>
          </div>

          <div className="settings-section">
            <h3 className="section-title">Range Display</h3>
            <span className="section-note">Configure range outline visibility and colours</span>

            {/* Daily Range - Enable + Colour on same row */}
            <div className="settings-row">
              <div className="row-label">Daily Range</div>
              <div className="row-controls">
                <div className="toggle-wrapper">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={colourSettings.showRangeOutline !== false}
                      onChange={(e) => handleToggleChange('showRangeOutline', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="toggle-label-text">
                    {colourSettings.showRangeOutline !== false ? 'Visible' : 'Hidden'}
                  </span>
                </div>
                <div className="colour-input-wrapper">
                  <input
                    type="color"
                    value={colourSettings.maxDistanceOutline}
                    onChange={(e) => handleColourChange('maxDistanceOutline', e.target.value)}
                    className="colour-picker"
                    disabled={colourSettings.showRangeOutline === false}
                  />
                  <input
                    type="text"
                    value={colourSettings.maxDistanceOutline}
                    onChange={(e) => handleColourChange('maxDistanceOutline', e.target.value)}
                    className="colour-text"
                    maxLength={7}
                    disabled={colourSettings.showRangeOutline === false}
                  />
                </div>
              </div>
            </div>

            {/* Per-receiver daily range reset */}
            {hasReceivers && (
              <div className="receiver-reset-list">
                <div className="receiver-reset-header">Clear Today's Range Data</div>
                {receivers.map(receiver => (
                  <div key={receiver.receiverUuid} className="receiver-reset-row">
                    <span className="receiver-name">{receiver.receiverName}</span>
                    <button
                      className="btn-reset-data"
                      onClick={() => showResetConfirmation('daily', receiver.receiverUuid, receiver.receiverName)}
                      title={`Clear today's range data for ${receiver.receiverName}`}
                    >
                      Clear Daily
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* All-Time Range - Enable + Colour on same row */}
            <div className="settings-row">
              <div className="row-label">All-Time Range</div>
              <div className="row-controls">
                <div className="toggle-wrapper">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={colourSettings.showAllTimeRangeOutline !== false}
                      onChange={(e) => handleToggleChange('showAllTimeRangeOutline', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="toggle-label-text">
                    {colourSettings.showAllTimeRangeOutline !== false ? 'Visible' : 'Hidden'}
                  </span>
                </div>
                <div className="colour-input-wrapper">
                  <input
                    type="color"
                    value={colourSettings.allTimeRangeOutline || '#8B4513'}
                    onChange={(e) => handleColourChange('allTimeRangeOutline', e.target.value)}
                    className="colour-picker"
                    disabled={colourSettings.showAllTimeRangeOutline === false}
                  />
                  <input
                    type="text"
                    value={colourSettings.allTimeRangeOutline || '#8B4513'}
                    onChange={(e) => handleColourChange('allTimeRangeOutline', e.target.value)}
                    className="colour-text"
                    maxLength={7}
                    disabled={colourSettings.showAllTimeRangeOutline === false}
                  />
                </div>
              </div>
            </div>

            {/* Per-receiver all-time range reset */}
            {hasReceivers && (
              <div className="receiver-reset-list receiver-reset-list-danger">
                <div className="receiver-reset-header">Clear All-Time Range Data</div>
                {receivers.map(receiver => (
                  <div key={receiver.receiverUuid} className="receiver-reset-row">
                    <span className="receiver-name">{receiver.receiverName}</span>
                    <button
                      className="btn-reset-data btn-reset-data-danger"
                      onClick={() => showResetConfirmation('allTime', receiver.receiverUuid, receiver.receiverName)}
                      title={`Clear all-time range data for ${receiver.receiverName}`}
                    >
                      Clear All-Time
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!hasReceivers && (
              <div className="reset-note">
                No receivers configured. Add a receiver to enable range data clearing.
              </div>
            )}
          </div>

          <div className="settings-section">
            <h3 className="section-title">Map Overlays</h3>
            <span className="section-note">Toggle map overlay layers</span>

            <div className="settings-row">
              <div className="row-label">NOTAMs</div>
              <div className="row-controls">
                <div className="toggle-wrapper">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={colourSettings.showNotams !== false}
                      onChange={(e) => handleToggleChange('showNotams', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="toggle-label-text">
                    {colourSettings.showNotams !== false ? 'Visible' : 'Hidden'}
                  </span>
                </div>
              </div>
            </div>

            <div className="settings-row">
              <div className="row-label">Ground vehicles</div>
              <div className="row-controls">
                <div className="toggle-wrapper">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={colourSettings.showGroundVehicles !== false}
                      onChange={(e) => handleToggleChange('showGroundVehicles', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="toggle-label-text">
                    {colourSettings.showGroundVehicles !== false ? 'Visible' : 'Hidden'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="section-title">Aircraft Colours</h3>
            <span className="section-note">Customise aircraft icon colours</span>

            <div className="settings-row">
              <div className="row-label">Normal</div>
              <div className="row-controls">
                <div className="colour-input-wrapper">
                  <input
                    type="color"
                    value={colourSettings.aircraftNormal}
                    onChange={(e) => handleColourChange('aircraftNormal', e.target.value)}
                    className="colour-picker"
                  />
                  <input
                    type="text"
                    value={colourSettings.aircraftNormal}
                    onChange={(e) => handleColourChange('aircraftNormal', e.target.value)}
                    className="colour-text"
                    maxLength={7}
                  />
                </div>
              </div>
            </div>

            <div className="settings-row">
              <div className="row-label">New Aircraft</div>
              <div className="row-controls">
                <div className="colour-input-wrapper">
                  <input
                    type="color"
                    value={colourSettings.aircraftNew}
                    onChange={(e) => handleColourChange('aircraftNew', e.target.value)}
                    className="colour-picker"
                  />
                  <input
                    type="text"
                    value={colourSettings.aircraftNew}
                    onChange={(e) => handleColourChange('aircraftNew', e.target.value)}
                    className="colour-text"
                    maxLength={7}
                  />
                </div>
              </div>
            </div>

            <div className="settings-row">
              <div className="row-label">Selected</div>
              <div className="row-controls">
                <div className="colour-input-wrapper">
                  <input
                    type="color"
                    value={colourSettings.aircraftSelected}
                    onChange={(e) => handleColourChange('aircraftSelected', e.target.value)}
                    className="colour-picker"
                  />
                  <input
                    type="text"
                    value={colourSettings.aircraftSelected}
                    onChange={(e) => handleColourChange('aircraftSelected', e.target.value)}
                    className="colour-text"
                    maxLength={7}
                  />
                </div>
              </div>
            </div>

            <button className="btn-reset" onClick={handleResetColours}>
              Reset to Defaults
            </button>
          </div>

          <div className="settings-section">
            <h3 className="section-title">About</h3>
            <span className="section-note">Application version and update management</span>

            <div className="about-version-info">
              <div className="about-row">
                <span className="about-label">Version</span>
                <span className="about-value">v{getCompiledVersion()}</span>
              </div>
              {getBuildTimestamp() && (
                <div className="about-row">
                  <span className="about-label">Built</span>
                  <span className="about-value">{new Date(getBuildTimestamp()).toLocaleString()}</span>
                </div>
              )}
            </div>

            <div className="about-actions">
              <button
                className="btn-reset"
                onClick={handleCheckForUpdates}
                disabled={updateStatus === 'checking' || updateStatus === 'reloading'}
              >
                {updateStatus === 'checking' ? 'Checking...' : 'Check for Updates'}
              </button>
              <button
                className="btn-reset"
                onClick={handleForceReload}
                disabled={updateStatus === 'reloading'}
              >
                {updateStatus === 'reloading' ? 'Reloading...' : 'Force Reload'}
              </button>
            </div>

            {updateStatus === 'up-to-date' && (
              <div className="about-status about-status-ok">
                You are running the latest version (v{getCompiledVersion()}).
              </div>
            )}
            {updateStatus === 'update-available' && (
              <div className="about-status about-status-update">
                A newer version{remoteVersion ? ` (v${remoteVersion})` : ''} is available.
                <button className="btn-update-now" onClick={handleForceReload}>
                  Update Now
                </button>
              </div>
            )}
            {updateStatus === 'reloading' && (
              <div className="about-status about-status-ok">
                Clearing caches and reloading&hellip;
              </div>
            )}
            {updateStatus === 'error' && (
              <div className="about-status about-status-error">Could not check for updates. You may be offline.</div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="modal-overlay" onClick={closeConfirmModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {confirmModal.type === 'allTime' ? 'Clear All-Time Range Data' : 'Clear Daily Range Data'}
              </h3>
              <button className="modal-close" onClick={closeConfirmModal}>&times;</button>
            </div>
            <div className="modal-body">
              {confirmModal.success ? (
                <div className="modal-success">
                  {confirmModal.success}
                </div>
              ) : (
                <>
                  <div className="modal-warning">
                    <strong>Are you sure?</strong>
                    <p>This action cannot be undone.</p>
                  </div>
                  <p className="modal-description">
                    {confirmModal.type === 'allTime'
                      ? `This will permanently delete ALL historical range data for ${confirmModal.receiverName || 'this receiver'}. All max distance records will be cleared.`
                      : `This will permanently delete today's range data for ${confirmModal.receiverName || 'this receiver'}. The range outline will start fresh.`
                    }
                  </p>
                  {confirmModal.error && (
                    <div className="modal-error">
                      {confirmModal.error}
                    </div>
                  )}
                </>
              )}
            </div>
            {!confirmModal.success && (
              <div className="modal-footer">
                <button
                  className="btn-modal btn-modal-cancel"
                  onClick={closeConfirmModal}
                  disabled={confirmModal.loading}
                >
                  Cancel
                </button>
                <button
                  className={`btn-modal btn-modal-confirm ${confirmModal.type === 'allTime' ? 'btn-modal-danger' : ''}`}
                  onClick={handleConfirmedReset}
                  disabled={confirmModal.loading}
                >
                  {confirmModal.loading ? 'Clearing...' : 'Yes, Clear Data'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Column Configurator Modal */}
      <ColumnConfigurator
        isOpen={configuratorModal.isOpen}
        onClose={closeConfigurator}
        tableName={configuratorModal.tableName}
        tableTitle={configuratorModal.tableTitle}
        currentConfig={configuratorModal.tableName ? getTableConfig(configuratorModal.tableName) : null}
        onApply={handleApplyColumns}
      />
    </div>
  );
}

export default Settings;
